import axios from '../../../app/api-client.ts';
import clsx from 'clsx';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FieldInputValue } from '../../../types';
import { KTIcon } from '../../helpers';
import HTooltip from './HTooltip.tsx';
import {getApplicationGeneration} from '../../../app/application-runtime';
import {getSessionGeneration} from '../../../app/session-runtime';

export interface ActionButtonProps<T> {
  icon?: string | JSX.Element;
  title?: React.ReactNode;
  action: { path: string; method?: string };
  className?: string;
  onFinish?: (data: T) => void;
  onError?: (error: unknown) => void;
  data?: Record<string, FieldInputValue>;
  confirmation?: { title?: string; message: string; context?: {applicationName: string; environmentName: string; isLive: boolean} };
  tooltip?: string;
  'data-cy'?: string;
}

function ActionButton<T>({
  title,
  icon,
  className = 'btn btn-light fw-bold flex-shrink-0',
  action,
  onFinish,
  onError,
  data = {},
  confirmation,
  tooltip,
  'data-cy': dataCy,
}: ActionButtonProps<T>) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const btn = (
    <button
      type='button'
      disabled={loading}
      className={clsx(className)}
      data-cy={dataCy}
      onClick={() => {
        const applicationGeneration = getApplicationGeneration();
        const sessionGeneration = getSessionGeneration();
        const perform = () => {
          if (!mounted.current || applicationGeneration !== getApplicationGeneration() || sessionGeneration !== getSessionGeneration()) return;
          setLoading(true);
          if (action.method == 'delete' && data != null) {
            axios
              .request({ url: action.path, method: action.method, data })
              .then(({ data }) => {
                setLoading(false);
                if (onFinish) onFinish(data);
              })
              .catch((error) => {
                setLoading(false);
                onError?.(error);
              });
          } else {
            axios
              .request({
                url: action.path,
                method: action.method || 'post',
                data,
              })
              .then(({ data }) => {
                setLoading(false);
                if (onFinish) onFinish(data);
              })
              .catch((error) => {
                setLoading(false);
                onError?.(error);
              });
          }
        };
        if (confirmation) {
          const content = document.createElement('div');
          const message = document.createElement('p');
          message.textContent = confirmation.message;
          content.append(message);
          if (confirmation.context) {
            const application = document.createElement('strong');
            application.className = 'd-block mb-2';
            application.textContent = confirmation.context.applicationName;
            const environment = document.createElement('span');
            environment.className = `badge ${confirmation.context.isLive ? 'badge-light-success' : 'badge-light-warning'}`;
            environment.textContent = confirmation.context.environmentName;
            content.append(application, environment);
          }
          Swal.fire({
            title: confirmation.title || t('Confirmation'),
            html: content,
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: t('Yes_do_it'),
            showCancelButton: true,
            cancelButtonText: t('Cancel'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-primary',
              cancelButton: 'btn fw-bold btn-secondary',
            },
          }).then((value) => {
            if (value.isConfirmed) {
              perform();
            }
          });
        } else {
          perform();
        }
      }}
    >
      {!loading && (
        <>
          {icon && <KTIcon iconName={String(icon)} className={clsx('fs-3', {'pe-0': !title})} />}
          {title && title}
        </>
      )}
      {loading && (
        <span className='indicator-progress' style={{ display: 'block' }}>
          {className?.indexOf('btn-icon') < 0 && `${t('Please wait')}... `}
          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
        </span>
      )}
    </button>
  );
  if (tooltip) {
    return <HTooltip title={tooltip}>{btn}</HTooltip>;
  }
  return btn;
}

export default ActionButton;
